/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React from 'react';
import { getRowProperty } from '../utils';
import BaseCell from './BaseCell';
import DataCellCheckbox from './DataCellCheckbox';
import { SelectSingleRowAction, DataTableProps, DataRowRecord } from '../types';
import { nullFn } from '../utils';
import { classNames } from '../../../utils';
import LoadingCell from '../../loader/components/LoadingCell';

type DProps<T> = Pick<
  DataTableProps<T>,
  | 'columns'
  | 'keyField'
  | 'selectableRows'
  | 'selectableRowsHighlight'
  | 'selectableRowsDisabled'
>;

interface DataRowProps<T> extends Required<DProps<T>> {
  id: string | number;
  onSelectedRow: (action: SelectSingleRowAction<T>) => void;
  singleSelect: boolean;
  selectedCount: number;
  row: T;
  rowCount: number;
  rowIdx: number;
  selected: boolean;
}

function DataTableRow<T extends DataRowRecord>({
  columns = [],
  id,
  keyField = 'id',
  onSelectedRow = nullFn,
  singleSelect,
  selectedCount,
  row,
  rowIdx,
  rowCount,
  selectableRows = false,
  selectableRowsDisabled = null,
  selectableRowsHighlight = false,
  selected,
}: DataRowProps<T>): JSX.Element {
  const highlightSelected = selectableRowsHighlight && selected;

  const isSelectable =
    typeof selectableRows === 'function' ? selectableRows(row) : selectableRows;

  const buildRow = () => {
    if (typeof selectableRows === 'function') {
      if (isSelectable) {
        return (
          <DataCellCheckbox
            selectableRowDisabled={selectableRowsDisabled}
            singleSelect={singleSelect}
            selectedCount={selectedCount}
            rowCount={rowCount}
            row={row}
            selected={selected}
            onSelected={onSelectedRow}
            keyField='id'
          />
        );
      } else {
        return <div style={{ flex: '0 0 48px' }}></div>;
      }
    } else {
      if (isSelectable) {
        return (
          <DataCellCheckbox
            selectableRowDisabled={selectableRowsDisabled}
            singleSelect={singleSelect}
            selectedCount={selectedCount}
            rowCount={rowCount}
            row={row}
            selected={selected}
            onSelected={onSelectedRow}
            keyField='id'
          />
        );
      } else {
        return <div style={{ flex: '0 0 10px' }}></div>;
      }
    }
  };

  return (
    <div
      id={`row-${id}`}
      role='row'
      className={classNames(
        'box-border flex w-full items-stretch px-8 py-6 last:mb-0',
        highlightSelected
          ? 'bg-green-lightest'
          : rowIdx % 2 !== 0
          ? 'bg-gray-lightest'
          : 'bg-transparent',
      )}
      style={{ alignContent: 'stretch' }}
    >
      {buildRow()}
      {columns.map((col, idx) => {
        const dataTag = col.button ? null : 'allowRowEvents';

        return (
          <BaseCell
            id={`cell-${col.id || idx + 1}-${row[keyField]}`}
            key={`cell-${col.id || idx + 1}-${row[keyField]}`}
            dataTag={dataTag}
            button={col.button}
            center={col.button}
            maxWidth={col.maxWidth}
            minWidth={col.minWidth}
            right={col.right}
            width={col.width}
          >
            <LoadingCell isLoading={col.loading ? col.loading : false}>
              {!col.cell && (
                <div>{getRowProperty(row, col.selector, rowIdx)}</div>
              )}
              {col.cell && col.cell(row, idx, col, idx)}
            </LoadingCell>
          </BaseCell>
        );
      })}
    </div>
  );
}

export default DataTableRow;
